import React, { useMemo, useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Clock, Play, StopCircle, ArrowLeft } from 'lucide-react';
import { format, differenceInSeconds } from 'date-fns';
import { useDetailTracking } from '@/hooks/trackingTime';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import clsx from 'clsx';
import ActivityLevel from './activity-level';
import DailyActivitySummary from './daily-activity-summary';

const DetailTracking: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data, isLoading, date, handleSetDate, isMonthly, handleToggleMonthly } = useDetailTracking(id || '');
    const [expandedTrackingId, setExpandedTrackingId] = useState<string | null>(null);

    // Memoize the format time function
    const formatTime = useCallback((time: number) => {
        const hours = Math.floor(time / 60);
        const minutes = time % 60;
        return `${hours}h ${minutes}m`;
    }, []);

    // Memoize the formatted date for input value
    const dateInputValue = useMemo(() => {
        if (isMonthly) {
            return format(new Date(date), 'yyyy-MM');
        }
        return date;
    }, [date, isMonthly]);

    // Memoize the input type
    const inputType = useMemo(() => isMonthly ? 'month' : 'date', [isMonthly]);

    // Memoize the tracking details rendering
    const trackingDetails = useMemo(() => {
        if (!data?.detailTracking || data.detailTracking.length === 0) {
            return (
                <p className="text-center text-sm sm:text-base text-muted-foreground">
                    No tracking details found
                </p>
            );
        }

        // Sort tracking details by creation date - newest first (descending order)
        // TODO: sort at backend
        const sortedDetails = [...data.detailTracking].sort((a, b) => {
            const dateA = new Date(a.trackingTimeDetail.createdAt).getTime();
            const dateB = new Date(b.trackingTimeDetail.createdAt).getTime();
            return dateB - dateA; // Descending order (newest first)
        });

        return sortedDetails.map((detail, index) => {
            const isEven = index % 2 === 0;
            const startTime = new Date(detail.trackingTimeDetail.createdAt);
            let endTime: Date | null = new Date(detail.trackingTimeDetail.updatedAt);
            let isPending = false;

            if (
                detail.trackingTimeDetail.status === 'working' ||
                detail.trackingTimeDetail.createdAt === detail.trackingTimeDetail.updatedAt
            ) {
                endTime = null;
                isPending = true;
            }

            const now = new Date();
            const durationSeconds = differenceInSeconds(
                isPending ? now : new Date(detail.trackingTimeDetail.updatedAt),
                startTime
            );
            const durationMinutes = Math.floor(durationSeconds / 60);
            const trackingTimeId = detail.trackingTimeDetail.trackingTimeId || detail.trackingTimeDetail._id;
            const detailId = detail.trackingTimeDetail._id; // Use unique detail ID for expand/collapse
            const isExpanded = expandedTrackingId === detailId;

            return (
                <React.Fragment key={detail.trackingTimeDetail._id}>
                    <div className={clsx(
                        'p-3 sm:p-4 rounded-md border space-y-2',
                        detail.trackingTimeDetail.status === 'working' && 'bg-green-500',
                        { 'bg-slate-100': isEven }
                    )}>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                            <div>
                                <p className="text-sm font-medium">{detail.task}</p>
                                <p className="text-xs text-muted-foreground">
                                    {format(new Date(detail.trackingTimeDetail.createdAt), 'dd/MM/yyyy HH:mm:ss')}
                                </p>
                                <p className="text-xs text-muted-foreground">{detail.project}</p>
                            </div>
                            <div className="text-sm text-right">
                                <p className="text-muted-foreground">Duration</p>
                                <p>{formatTime(durationMinutes)}</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center">
                                    <Play className="h-3 w-3 mr-1" />
                                    {format(startTime, 'HH:mm:ss')}
                                </div>
                                <div className="flex items-center">
                                    <StopCircle className="h-3 w-3 mr-1" />
                                    {isPending ? 'Pending' : format(endTime as Date, 'HH:mm:ss')}
                                </div>
                            </div>
                            {trackingTimeId && data?.user?._id && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setExpandedTrackingId((current) =>
                                            current === detailId ? null : detailId,
                                        )
                                    }
                                >
                                    {isExpanded ? 'Hide Activity' : 'Show Activity'}
                                </Button>
                            )}
                        </div>
                        {/* Activity Level for this tracking session (lazy-loaded on demand) */}
                        {isExpanded && trackingTimeId && data?.user?._id && detailId === expandedTrackingId && (
                            <div className="mt-3 pt-3 border-t">
                                <ActivityLevel
                                    trackingTimeId={trackingTimeId}
                                    variant="inline"
                                    timeRange={
                                        endTime
                                            ? {
                                                  start: startTime,
                                                  end: endTime,
                                              }
                                            : undefined
                                    }
                                    uid={data.user._id}
                                    date={date}
                                />
                            </div>
                        )}
                    </div>
                    {index < sortedDetails.length - 1 && <Separator className="my-4" />}
                </React.Fragment>
            );
        });
    }, [data?.detailTracking, data?.user?._id, date, formatTime, expandedTrackingId]);

    const handleBackClick = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    if (isLoading) {
        return (
            <div className="container mx-auto p-2 sm:p-6 space-y-4 sm:space-y-6">
                <Card>
                    <CardHeader className="space-y-2">
                        <Skeleton className="h-8 w-[200px]" />
                        <Skeleton className="h-4 w-[300px]" />
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            <Card>
                                <CardHeader className="space-y-2">
                                    <Skeleton className="h-6 w-[150px]" />
                                    <Skeleton className="h-4 w-[200px]" />
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-[80%]" />
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="space-y-2">
                                    <Skeleton className="h-6 w-[150px]" />
                                </CardHeader>
                                <CardContent>
                                    <Skeleton className="h-4 w-full" />
                                </CardContent>
                            </Card>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="container mx-auto p-2 sm:p-6">
                <Card>
                    <CardContent className="flex justify-center items-center h-[400px]">
                        <div className="text-center space-y-4">
                            <p className="text-muted-foreground text-sm sm:text-base">No tracking data found</p>
                            <Button onClick={handleBackClick} variant="outline">
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Go Back
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const { user, trackingTime, totalTime } = data;

    return (
        <div className="container mx-auto p-2 sm:p-6 space-y-4 sm:space-y-6">
            <Card>
                <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
                    <div className="flex items-center space-x-3">
                        <Button onClick={handleBackClick} variant="ghost" size="icon">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div>
                            <CardTitle className="text-xl sm:text-2xl">Tracking Time Details</CardTitle>
                            <p className="text-sm sm:text-base text-muted-foreground">
                                Detailed tracking information for {user.name}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="monthly"
                                checked={isMonthly}
                                onCheckedChange={handleToggleMonthly}
                            />
                            <Label htmlFor="monthly" className="text-sm">Monthly View</Label>
                        </div>
                        <Input
                            type={inputType}
                            value={dateInputValue}
                            onChange={(e) => handleSetDate(e.target.value)}
                            className="w-full sm:w-[200px]"
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        {/* User Information Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg sm:text-xl">User Information</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center space-x-4">
                                    <Avatar className="h-16 w-16">
                                        <AvatarImage
                                            src={user.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}`}
                                            alt={user.name}
                                        />
                                        <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <h3 className="text-lg font-semibold">{user.name}</h3>
                                        <p className="text-sm text-muted-foreground">{user.email}</p>
                                        <Badge variant={user.isWorking ? "default" : "secondary"} className="mt-1">
                                            {user.isWorking ? "Working" : "Not Working"}
                                        </Badge>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Time Summary Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg sm:text-xl">Time Summary</CardTitle>
                                <p className="text-sm text-muted-foreground">
                                    {isMonthly ? format(new Date(date), 'MMMM yyyy') : format(new Date(date), 'dd/MM/yyyy')}
                                </p>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    <div className="flex items-center">
                                        <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm sm:text-base">Total Time: {formatTime(totalTime)}</span>
                                    </div>
                                    {trackingTime?.monitoring && (
                                        <>
                                            <div className="flex items-center">
                                                <span className="text-sm sm:text-base text-muted-foreground">Current Task:</span>
                                                <span className="ml-2 text-sm sm:text-base">{trackingTime.monitoring.taskName || 'No task'}</span>
                                            </div>
                                            <div className="flex items-center">
                                                <span className="text-sm sm:text-base text-muted-foreground">Current Project:</span>
                                                <span className="ml-2 text-sm sm:text-base">{trackingTime.monitoring.projectName || 'No project'}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Tracking Details */}
                        <Card className="md:col-span-2">
                            <CardHeader>
                                <CardTitle className="text-lg sm:text-xl">Tracking Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ScrollArea className="h-[300px] sm:h-[400px] w-full rounded-md border p-2 sm:p-4">
                                    {trackingDetails}
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    </div>
                </CardContent>
            </Card>

            {/* Daily Activity Summary */}
            {id && (
                <DailyActivitySummary uid={id} date={date} />
            )}
        </div>
    );
};

export default React.memo(DetailTracking);